import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderService, type Order } from '../services/orderService';
import { uploadService } from '../services/uploadService';
import { useToast } from '../contexts/ToastContext';

const Enquiries: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [uploadingProof, setUploadingProof] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  // Load orders on component mount or when page changes
  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user, page]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await orderService.getUserOrders({
        page,
        limit: 10
      });
      
      if (response.success && response.data) {
        setOrders(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalOrders(response.data.total);
      } else {
        setError(response.error || 'Failed to load orders');
      }
    } catch (err) {
      setError('Failed to load orders');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'unpaid') return order.paymentStatus === 'pending' && (!order.paymentProofs || order.paymentProofs.length === 0);
    if (filter === 'pending_verification') return order.paymentStatus === 'pending' && order.paymentProofs && order.paymentProofs.length > 0;
    if (filter === 'paid') return order.paymentStatus === 'paid';
    return false;
  });

  // Unused function - commented out to fix build
  // const getStatusColor = (status: string) => {
  //   switch (status) {
  //     case 'delivered':
  //       return 'bg-green-100 text-green-700';
  //     case 'shipped':
  //       return 'bg-blue-100 text-blue-700';
  //     case 'processing':
  //       return 'bg-yellow-100 text-yellow-700';
  //     case 'cancelled':
  //       return 'bg-red-100 text-red-700';
  //     default:
  //       return 'bg-neutral-100 text-neutral-700';
  //   }
  // };

  const OrderIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );

  const filters = [
    { value: 'all', label: 'All Enquiries' },
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'pending_verification', label: 'Pending Verification' },
    { value: 'paid', label: 'Paid' },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-8 animate-fade-in-up">
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-1 sm:mb-2">My Enquiries</h1>
          <p className="text-sm sm:text-base text-neutral-600">Track and manage your enquiries</p>
        </div>

        {/* Filters */}
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          <div className="flex flex-wrap gap-2">
            {filters.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setFilter(value)}
                className={`px-3 py-2 text-sm sm:px-4 sm:text-base rounded-xl font-medium transition-all duration-200 ${
                  filter === value
                    ? 'bg-accent-600 text-white shadow-lg shadow-accent-500/25'
                    : 'bg-white text-neutral-700 border border-neutral-200 hover:border-accent-200 hover:text-accent-600'
                }`}
              >
                {label}
                {value === 'all' && (
                  <span className="ml-1 text-xs sm:text-sm opacity-75">({totalOrders})</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 sm:p-12 text-center animate-fade-in-up">
            <div className="animate-spin w-12 h-12 border-4 border-accent-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-neutral-600">Loading your enquiries...</p>
          </div>
        ) : error ? (
          /* Error State */
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 sm:p-12 text-center animate-fade-in-up">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">Failed to load enquiries</h3>
            <p className="text-neutral-600 mb-6">{error}</p>
            <button
              onClick={loadOrders}
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 text-white rounded-xl font-semibold hover:bg-accent-700 hover:shadow-xl hover:shadow-accent-500/25 transition-all duration-300"
            >
              Try Again
            </button>
          </div>
        ) : filteredOrders.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 sm:p-12 text-center animate-fade-in-up">
            <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <OrderIcon />
            </div>
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">No enquiries found</h3>
            <p className="text-neutral-600 mb-6">
              {filter === 'all' 
                ? "You haven't placed any enquiries yet." 
                : `No ${filter} enquiries found.`}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 text-white rounded-xl font-semibold hover:bg-accent-700 hover:shadow-xl hover:shadow-accent-500/25 transition-all duration-300"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredOrders.map((order, index) => (
              <div
                key={order.id}
                className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-4 sm:p-6 lg:p-8 hover:shadow-lg transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${(index + 2) * 100}ms` }}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  {/* Left Section - Order Info */}
                  <div className="flex-1">
                    {/* Order Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-neutral-900">{order.orderNumber}</h3>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                        order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                        order.paymentProofs && order.paymentProofs.length > 0 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {order.paymentStatus === 'paid' ? 'Paid' :
                         order.paymentProofs && order.paymentProofs.length > 0 ? 'Pending Verification' :
                         'Unpaid'}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-neutral-600 mb-3">
                      Enquired on {new Date(order.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    
                    {/* Amount Info */}
                    <div className="mb-4 lg:mb-0">
                      <p className="text-xs sm:text-sm text-neutral-600">Total Amount</p>
                      <p className="text-lg sm:text-xl lg:text-2xl font-bold text-neutral-900">₹{order.total.toLocaleString()}</p>
                      {order.packing > 0 && (
                        <p className="text-xs sm:text-sm text-neutral-600 font-medium">
                          Includes ₹{order.packing.toLocaleString()} packaging charge
                        </p>
                      )}
                      {order.couponCode && order.discount > 0 && (
                        <p className="text-xs sm:text-sm text-green-600 font-medium">
                          Saved ₹{order.discount.toLocaleString()} ({order.couponCode})
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Section - Action Button */}
                  <div className="flex items-start lg:items-center">
                    <Link
                      to={`/enquiries/${order.id}`}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-accent-600 text-white rounded-xl font-medium hover:bg-accent-700 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Details
                    </Link>
                  </div>
                </div>


                {/* Delivery Info */}
                <div className="mt-4 pt-4 border-t border-neutral-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="sm:col-span-2 lg:col-span-2">
                      <p className="text-xs sm:text-sm text-neutral-600 mb-1">Delivery Address</p>
                      <p className="text-sm sm:text-base font-medium text-neutral-900">{order.shippingAddress.street}</p>
                      <p className="text-xs sm:text-sm text-neutral-600">
                        {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.zipCode}
                      </p>
                    </div>
                    {order.trackingNumber && (
                      <div>
                        <p className="text-xs sm:text-sm text-neutral-600 mb-1">Tracking Number</p>
                        <p className="text-sm sm:text-base font-mono font-medium text-neutral-900">{order.trackingNumber}</p>
                      </div>
                    )}
                    {/* <div className="text-right">
                        <p className="text-sm text-neutral-600">Payment Method</p>
                        <div className="flex items-center gap-2 justify-end">
                          {order.paymentMethod === 'upi' && (
                            <>
                              <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-blue-600 rounded flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M21 7.28V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-2.28c.59-.35 1-.98 1-1.72V9c0-.74-.41-1.38-1-1.72zM20 9v6h-7V9h7zM5 19V5h14v2h-6c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h6v2H5z"/>
                                  <circle cx="16" cy="12" r="1.5"/>
                                </svg>
                              </div>
                              <span className="font-medium text-neutral-900">UPI</span>
                            </>
                          )}
                          {order.paymentMethod === 'cash' && (
                            <>
                              <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                                </svg>
                              </div>
                              <span className="font-medium text-neutral-900">Cash</span>
                            </>
                          )}
                          {order.paymentMethod === 'card' && (
                            <>
                              <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                                </svg>
                              </div>
                              <span className="font-medium text-neutral-900">Card</span>
                            </>
                          )}
                          {order.paymentMethod === 'net_banking' && (
                            <>
                              <div className="w-6 h-6 bg-gradient-to-r from-teal-500 to-cyan-600 rounded flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z"/>
                                </svg>
                              </div>
                              <span className="font-medium text-neutral-900">Net Banking</span>
                            </>
                          )}
                          {order.paymentMethod === 'wallet' && (
                            <>
                              <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-600 rounded flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                                </svg>
                              </div>
                              <span className="font-medium text-neutral-900">Wallet</span>
                            </>
                          )}
                          {order.paymentMethod === 'cheque' && (
                            <>
                              <div className="w-6 h-6 bg-gradient-to-r from-gray-500 to-gray-700 rounded flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                                </svg>
                              </div>
                              <span className="font-medium text-neutral-900">Cheque</span>
                            </>
                          )}
                          {order.paymentMethod === 'mixed' && (
                            <>
                              <div className="w-6 h-6 bg-gradient-to-r from-pink-500 to-purple-600 rounded flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                                </svg>
                              </div>
                              <span className="font-medium text-neutral-900">Mixed</span>
                            </>
                          )}
                        </div>
                      </div> */}
                  </div>
                </div>

                {/* Payment Proofs Section */}
                {((order.paymentProofs && order.paymentProofs.length > 0) || (order.status === 'pending' && order.paymentStatus !== 'paid')) && (
                  <div className="mt-4 pt-4 border-t border-neutral-100">
                    {/* Show existing payment proofs */}
                    {order.paymentProofs && order.paymentProofs.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs sm:text-sm font-medium text-neutral-700 mb-2">Payment Proofs ({order.paymentProofs.length})</p>
                        <div className="space-y-2">
                          {order.paymentProofs.map((proof, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                              {/* Preview thumbnail */}
                              {proof.url && (
                                <div 
                                  className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded overflow-hidden border border-green-300 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => window.open(proof.url, '_blank')}
                                >
                                  <img
                                    src={proof.url}
                                    alt={proof.fileName || `Payment proof ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              
                              {/* File info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <svg className="w-3 h-3 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                  </svg>
                                  <span 
                                    className="text-xs text-green-700 truncate cursor-pointer hover:text-green-800 max-w-[120px] sm:max-w-none"
                                    onClick={() => window.open(proof.url, '_blank')}
                                    title={proof.fileName || `Payment proof ${index + 1}`}
                                  >
                                    {proof.fileName || `Payment proof ${index + 1}`}
                                  </span>
                                </div>
                                {proof.uploadedAt && (
                                  <p className="text-xs text-green-600 ml-4 hidden sm:block">
                                    {new Date(proof.uploadedAt).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                )}
                              </div>
                              
                              {/* Status */}
                              {proof.status && (
                                <span className={`text-xs px-1.5 sm:px-2 py-0.5 rounded font-medium flex-shrink-0 ${
                                  proof.status === 'verified' ? 'bg-green-100 text-green-800' : 
                                  proof.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {proof.status === 'verified' ? 'Verified' : 
                                   proof.status === 'pending' ? 'Pending' : 
                                   proof.status}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Upload button - only show for pending orders */}
                    {order.status === 'pending' && (
                      <button
                        onClick={() => {
                          setUploadingProof(order.id);
                          fileInputRef.current?.click();
                        }}
                        disabled={uploadingProof === order.id}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-2 text-sm sm:px-4 sm:text-base bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploadingProof === order.id ? (
                          <>
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <span className="text-sm sm:text-base">{order.paymentProofs && order.paymentProofs.length > 0 ? 'Add More Payment Proof' : 'Upload Payment Proof'}</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}

              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="mt-8 flex justify-center gap-1 sm:gap-2 animate-fade-in-up">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className={`px-2 sm:px-4 py-2 text-sm sm:text-base rounded-xl font-medium transition-all duration-200 ${
                page === 1
                  ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                  : 'bg-white text-neutral-700 border border-neutral-200 hover:border-accent-200 hover:text-accent-600'
              }`}
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            <div className="flex items-center gap-1 sm:gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base rounded-xl font-medium transition-all duration-200 ${
                      page === pageNum
                        ? 'bg-accent-600 text-white shadow-lg shadow-accent-500/25'
                        : 'bg-white text-neutral-700 border border-neutral-200 hover:border-accent-200 hover:text-accent-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className={`px-2 sm:px-4 py-2 text-sm sm:text-base rounded-xl font-medium transition-all duration-200 ${
                page === totalPages
                  ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                  : 'bg-white text-neutral-700 border border-neutral-200 hover:border-accent-200 hover:text-accent-600'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Hidden File Input for Payment Proof Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          const orderId = uploadingProof;
          
          if (!file || !orderId) {
            e.target.value = '';
            return;
          }

          try {
            // Upload the file
            const uploadResponse = await uploadService.uploadPaymentProof(file);
            
            if (!uploadResponse.success || !uploadResponse.data?.url) {
              throw new Error('Failed to upload payment proof');
            }

            // Update order with payment proof
            const updateResponse = await orderService.uploadPaymentProof(orderId, {
              url: uploadResponse.data.url,
              fileName: file.name,
              fileType: file.type
            });

            if (updateResponse.success) {
              showToast('Payment proof uploaded successfully!', 'success');
              // Reload orders to show updated status
              loadOrders();
            } else {
              throw new Error(updateResponse.error || 'Failed to attach payment proof');
            }
          } catch (error) {
            console.error('Error uploading payment proof:', error);
            showToast(
              error instanceof Error ? error.message : 'Failed to upload payment proof',
              'error'
            );
          } finally {
            setUploadingProof(null);
            e.target.value = ''; // Reset file input
          }
        }}
      />
    </div>
  );
};

export default Enquiries;